import { useRef, useState } from 'react';

import { v4 as uuidv4 } from 'uuid';

import { Col, Row } from 'antd';

import { CustomNotification, Form, StyledModal } from 'common/components';

import {
  AllergenGroupForm,
  AllergenGroupWarning,
  defaultModalProps,
} from './AllergenGroup';

import QaAllergenGrid from './QaAllergenGrid';
import QaAllergenRegional from './QaAllergenRegional';

import CustomAllergenAddition from './CustomAllergenAddition';
import FormAllergen from './FormAllergen';

import { useGetProductEnums } from 'hooks';
import { useGetEnumOptions } from 'pages/qa-spec/hooks';
import { useGetAllergenGridHandlers } from 'pages/qa-spec/hooks/useAllergen';
import { useDisplayDefaultLatex, useSaveAllergenGroup } from './hooks';

import {
  DEFAULT_US_ALLERGENS,
  DEFAULT_US_LATEX_ALLERGENS,
  DEFAULT_WW_ALLERGENS,
  DEFAULT_WW_LATEX_ALLERGENS,
  US_VALUE,
  US_VALUE_LOWERCASE,
  WORLDWIDE_LOWERCASE,
} from './constants';

import { getAllRows } from 'pages/qa-spec/utils';
import { validateAllergenGrid } from 'pages/qa-spec/utils/allergen';
import { sleep } from 'utils/delay';
import { addIdToAllergens } from './utils';

export const AllergenGroupAdd = (props) => {
  const {
    onCancel: offModalAddAllergenSection,
    productId,
    allergenStatement,
    allergensModel,
    ...rest
  } = props;

  const [formInstance] = Form.useForm();
  const [customAllergenForm] = Form.useForm();

  const [regional, setRegional] = useState(US_VALUE);

  const isHBCType = useDisplayDefaultLatex();

  const defaultUSAllergensWithLatex = [
    ...DEFAULT_US_ALLERGENS,
    ...(isHBCType ? DEFAULT_US_LATEX_ALLERGENS : []),
  ];

  const defaultWWAllergenWithLatex = [
    ...DEFAULT_WW_ALLERGENS,
    ...(isHBCType ? DEFAULT_WW_LATEX_ALLERGENS : []),
  ];

  const [tempAllergensGrid, setTempAllergensGrid] = useState({
    us: addIdToAllergens(defaultUSAllergensWithLatex),
    ww: addIdToAllergens(defaultWWAllergenWithLatex),
  });

  const gridInstance = useRef();
  const allergenOptions = useGetEnumOptions()?.allergen;
  const { productEnums } = useGetProductEnums();

  const allergenMutation = useSaveAllergenGroup(productId);

  const { onCellEditingStopped, onCellEditingStarted, onRowClicked } =
    useGetAllergenGridHandlers(gridInstance);

  const isUs = regional.toLowerCase() === US_VALUE_LOWERCASE;
  const isWW = regional.toLowerCase() === WORLDWIDE_LOWERCASE;

  const defaultAllergensGrid = isUs
    ? tempAllergensGrid['us']
    : tempAllergensGrid['ww'];

  const handleChangeRegional = (regional) => {
    gridInstance.current.api.stopEditing();

    const allergenRows = getAllRows(gridInstance);

    const isGridHaveError =
      validateAllergenGrid(gridInstance).checkAllergensGridHaveError();

    if (isGridHaveError) {
      CustomNotification.error(`Please resolve allergen grid's errors`);
    } else {
      if (regional.toLowerCase() === WORLDWIDE_LOWERCASE) {
        setTempAllergensGrid((prevValue) => ({
          ...prevValue,
          us: allergenRows,
        }));
        setRegional(regional);
      } else {
        setTempAllergensGrid((prevValue) => ({
          ...prevValue,
          ww: allergenRows,
        }));
        setRegional(regional);
      }
    }
  };

  const handleAddAllergen = async () => {
    // const formValues = await formInstance.validateFields();
    const formValues = formInstance.getFieldsValue(true);

    // if (!formValues) return;

    const {
      allergenStatement,
      allergenSpecificationAgency,
      allergenSpecificationName,
    } = formValues;

    gridInstance.current.api.stopEditing();

    await sleep(100);

    const allergenRows = getAllRows(gridInstance);

    const isGridHaveError =
      validateAllergenGrid(gridInstance).checkAllergensGridHaveError();

    if (isGridHaveError) {
      CustomNotification.error(`Please resolve allergen grid's errors`);
      return;
    }

    const newAllergen = {
      allergenSpecificationAgency: allergenSpecificationAgency ?? null,
      allergenSpecificationName: allergenSpecificationName ?? null,
      allergenUnitedStates: isUs ? allergenRows : tempAllergensGrid['us'],
      allergenWorldWide: isWW ? allergenRows : tempAllergensGrid['ww'],
      index: null,
    };

    const params = {
      productId,
      allergenStatement,
      allergensModel: allergensModel.concat([newAllergen]),
    };

    allergenMutation.mutate(params, {
      onSuccess: (response) => {
        if (response?.isSuccess) {
          CustomNotification.success('Add Allergen successfully!');
          offModalAddAllergenSection();
        }
      },
    });
  };

  const handleUpdateDraftAllergen = async () => {
    const newId = uuidv4();

    gridInstance.current.api.stopEditing();

    await sleep();

    const allRowData = getAllRows(gridInstance);
    const newRowIndex = allRowData?.length;
    const {
      AllergenTypeCode: allergenType,
      ContainmentSource: containmentSource,
      LevelOfContainmentCode: levelOfContainment,
    } = customAllergenForm.getFieldsValue();

    gridInstance.current.api.applyTransaction({
      add: [
        {
          uuid: newId,
          error: [],
          isPresent: true,
          isPublish: true,
          regional,
          allergenType,
          containmentSource,
          levelOfContainment,
        },
      ],
      addIndex: newRowIndex,
    });

    gridInstance.current.api.ensureIndexVisible(newRowIndex);
    customAllergenForm.resetFields();
  };

  return (
    <StyledModal
      {...defaultModalProps}
      {...rest}
      isLoading={allergenMutation.isLoading}
      onCancel={offModalAddAllergenSection}
      onOk={handleAddAllergen}
      okButtonProps={{
        loading: allergenMutation.isLoading,
      }}
    >
      <AllergenGroupForm
        form={formInstance}
        initialValues={{
          allergenStatement,
        }}
        productId={productId}
      >
        <AllergenGroupWarning />
        <QaAllergenRegional
          value={regional}
          onChange={(value) => handleChangeRegional(value)}
        >
          <Row style={{ marginTop: 10, height: 300 }}>
            <Col xs={20}>
              <QaAllergenGrid
                gridInst={gridInstance}
                data={defaultAllergensGrid}
                isEdit={true}
                allergenOptions={allergenOptions}
                onCellEditingStopped={onCellEditingStopped}
                onCellEditingStarted={onCellEditingStarted}
                onRowClicked={onRowClicked}
              />
            </Col>

            <Col
              style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-end',
                marginLeft: 6,
              }}
            >
              <Form form={customAllergenForm}>
                <CustomAllergenAddition
                  gridInstance={gridInstance}
                  regional={regional}
                  productId={productId}
                  onAddCustomAllergen={handleUpdateDraftAllergen}
                >
                  <FormAllergen
                    productEnums={productEnums}
                    isUSRegion={isUs}
                    isWWRegion={isWW}
                  />
                </CustomAllergenAddition>
              </Form>
            </Col>
          </Row>
        </QaAllergenRegional>
      </AllergenGroupForm>
    </StyledModal>
  );
};
